import { useEffect, useRef, useState } from 'react';
import {
  Article,
  CalendarBlank,
  CheckCircle,
  FilePdf,
  FilmStrip,
  Images,
  Palette,
  PaperPlaneTilt,
  Sparkle,
  UsersThree,
} from '@phosphor-icons/react';

const ICONS = [UsersThree, Palette, Article, Sparkle, PaperPlaneTilt];

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!query) return undefined;
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);

  return reduced;
}

function ClientStage() {
  return (
    <div className="grid h-full gap-3 md:grid-cols-[1.15fr_.85fr]">
      <div className="rounded-2xl border border-white/[.07] bg-[#111b30] p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-[.18em] text-indigo-300">Client Brain</p>
            <p className="mt-1 text-base font-semibold">Atelier Forma</p>
          </div>
          <span className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-2 py-1 text-[9px] text-emerald-300">attivo</span>
        </div>
        <div className="mt-5 rounded-xl border border-white/[.06] bg-white/[.025] p-4">
          <p className="text-[9px] font-semibold uppercase tracking-[.14em] text-white/35">Chi è</p>
          <p className="mt-2 text-xs leading-5 text-slate-400">Studio creativo premium. Voce diretta, colta e contemporanea.</p>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {['Autorevolezza', 'Chiarezza', 'Premium', 'Contemporaneo'].map(item => (
            <div key={item} className="rounded-lg border border-white/[.05] bg-white/[.02] px-3 py-2 text-[10px] text-slate-300">{item}</div>
          ))}
        </div>
      </div>
      <div className="space-y-3">
        <div className="rounded-2xl border border-fuchsia-400/20 bg-fuchsia-400/[.06] p-4">
          <p className="text-[9px] font-bold uppercase tracking-[.16em] text-fuchsia-300">Campagna</p>
          <p className="mt-2 text-sm font-semibold">Settembre 2026</p>
          <div className="mt-4 space-y-2">
            {[92, 76, 84].map(width => <div key={width} className="h-1.5 rounded-full bg-white/10" style={{ width: `${width}%` }} />)}
          </div>
        </div>
        <div className="rounded-2xl border border-white/[.07] bg-[#111b30] p-4">
          <p className="text-[9px] font-bold uppercase tracking-[.16em] text-cyan-300">Memoria</p>
          <p className="mt-2 text-[10px] leading-5 text-slate-400">Contesto, tono e priorità restano collegati ai contenuti successivi.</p>
        </div>
      </div>
    </div>
  );
}

function DnaStage() {
  return (
    <div className="grid h-full gap-3 md:grid-cols-[.8fr_1.2fr]">
      <div className="rounded-2xl border border-white/[.07] bg-[#111b30] p-5">
        <p className="text-[9px] font-bold uppercase tracking-[.18em] text-indigo-300">Design DNA</p>
        <div className="mt-4 flex gap-2">
          {['#111827', '#8B5CF6', '#EC4899', '#F8FAFC'].map(value => (
            <span key={value} className="h-9 flex-1 rounded-lg border border-white/10" style={{ backgroundColor: value }} />
          ))}
        </div>
        <div className="mt-5 space-y-3 text-[10px] text-slate-400">
          <div className="flex justify-between"><span>H1</span><span className="text-white">Inter / 72</span></div>
          <div className="flex justify-between"><span>Paragraph</span><span className="text-white">Inter / 28</span></div>
          <div className="flex justify-between"><span>Ancoraggio</span><span className="rounded-md bg-white/[.05] px-2 py-1 text-white">Bottom</span></div>
        </div>
      </div>
      <div className="flex items-center justify-center rounded-2xl border border-white/[.08] bg-[#0c1323] p-5">
        <div className="flex aspect-[4/5] h-[310px] max-h-full flex-col justify-end overflow-hidden rounded-xl border border-white/10 bg-gradient-to-br from-[#211339] via-[#111827] to-[#090d16] p-6 shadow-2xl shadow-black/30">
          <span className="mb-3 w-fit rounded-full border border-fuchsia-300/30 bg-fuchsia-400/10 px-2 py-1 text-[8px] font-semibold uppercase tracking-[.18em] text-fuchsia-200">Ritratto consapevole</span>
          <p className="text-3xl font-black leading-[.94] tracking-[-.045em]">La relazione cambia il ritratto.</p>
          <div className="mt-4 h-px w-12 bg-fuchsia-400" />
          <p className="mt-3 max-w-[88%] text-[10px] leading-4 text-white/55">Il contenuto entra nel sistema visivo, non il contrario.</p>
        </div>
      </div>
    </div>
  );
}

function MasterStage() {
  return (
    <div className="grid h-full gap-3 md:grid-cols-[1.25fr_.75fr]">
      <div className="rounded-2xl border border-white/[.07] bg-[#111b30] p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-[.18em] text-fuchsia-300">Content Master</p>
            <p className="mt-1 text-sm font-semibold">La relazione prima della posa</p>
          </div>
          <CheckCircle size={19} weight="fill" className="text-emerald-400" />
        </div>
        <div className="mt-5 space-y-3">
          <div className="h-2.5 w-[76%] rounded-full bg-white/15" />
          {[96, 88, 92, 72].map(width => <div key={width} className="h-1.5 rounded-full bg-white/[.07]" style={{ width: `${width}%` }} />)}
          <div className="my-5 h-px bg-white/[.06]" />
          <div className="h-2.5 w-[52%] rounded-full bg-white/12" />
          {[90, 83, 70].map(width => <div key={width} className="h-1.5 rounded-full bg-white/[.07]" style={{ width: `${width}%` }} />)}
        </div>
      </div>
      <div className="rounded-2xl border border-white/[.07] bg-[#111b30] p-4">
        <p className="text-[9px] font-bold uppercase tracking-[.16em] text-indigo-300">Deriva output</p>
        <div className="mt-3 space-y-2">
          {[[Images, 'Carousel'], [FilmStrip, 'Reel'], [FilePdf, 'PDF']].map(([Icon, label]) => (
            <div key={label} className="flex items-center gap-3 rounded-xl border border-white/[.06] bg-white/[.025] p-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-fuchsia-400/10 text-fuchsia-300"><Icon size={16} /></div>
              <span className="text-[10px] font-semibold">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Fan({ compact = false, open = true, reduced = false }) {
  const cards = [
    ['01', 'Perché il soggetto sembra rigido?'],
    ['02', 'Non è solo un problema tecnico.'],
    ['03', 'Costruisci fiducia prima di scattare.'],
    ['04', 'Un passo indietro crea sicurezza.'],
    ['05', 'La bellezza emerge senza forzare.'],
  ];
  const positions = [
    { x: -118, r: -14, y: 16 },
    { x: -58, r: -7, y: 5 },
    { x: 0, r: 0, y: 0 },
    { x: 58, r: 7, y: 5 },
    { x: 118, r: 14, y: 16 },
  ];

  return (
    <div className={`relative mx-auto ${compact ? 'h-[320px] w-[390px] max-w-full' : 'h-[390px] w-[520px] max-w-full'}`} aria-hidden="true">
      {cards.map(([number, title], index) => {
        const pose = positions[index];
        const transform = open || reduced
          ? `translate3d(${pose.x}px, ${pose.y}px, 0) rotate(${pose.r}deg) scale(1)`
          : 'translate3d(0, 34px, 0) rotate(0deg) scale(.9)';
        return (
          <div
            key={number}
            className={`absolute left-1/2 top-1/2 overflow-hidden rounded-xl border border-black/10 bg-gradient-to-br from-[#f8f4ee] to-[#ddd5cc] p-5 text-[#171717] shadow-2xl shadow-black/30 transition-all duration-700 ease-out ${compact ? 'h-[260px] w-[208px]' : 'h-[330px] w-[264px]'}`}
            style={{
              transform: `translate(-50%, -50%) ${transform}`,
              zIndex: index === 2 ? 10 : 5 - Math.abs(index - 2),
              transitionDelay: reduced ? '0ms' : `${index * 55}ms`,
            }}
          >
            <p className="text-[8px] font-bold uppercase tracking-[.18em] text-[#8a4f83]">Ritratto consapevole</p>
            <p className={`${compact ? 'mt-7 text-xl' : 'mt-9 text-2xl'} font-black leading-[.96] tracking-[-.045em]`}>{title}</p>
            <div className="mt-5 h-px w-10 bg-[#8a4f83]/60" />
            <p className="mt-4 text-[9px] leading-4 text-black/50">Ogni slide resta collegata allo stesso contenuto e allo stesso Design DNA.</p>
            <div className="absolute bottom-4 left-5 right-5 flex items-center justify-between border-t border-black/10 pt-3 text-[7px] font-semibold uppercase tracking-[.12em] text-black/35">
              <span>Sketchario</span><span>{number}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function OutputStage({ reduced }) {
  return (
    <div className="grid h-full gap-3 md:grid-cols-[.72fr_1.28fr] md:items-center">
      <div className="rounded-2xl border border-white/[.07] bg-[#111b30] p-5">
        <p className="text-[9px] font-bold uppercase tracking-[.18em] text-fuchsia-300">Output collegati</p>
        <div className="mt-4 space-y-2">
          {[[Images, 'Carousel'], [FilmStrip, 'Reel'], [FilePdf, 'PDF']].map(([Icon, label], index) => (
            <div key={label} className={`flex items-center gap-3 rounded-xl border p-3 ${index === 0 ? 'border-fuchsia-400/30 bg-fuchsia-400/[.07]' : 'border-white/[.06] bg-white/[.025]'}`}>
              <Icon size={17} className="text-fuchsia-300" />
              <span className="text-[10px] font-semibold">{label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="overflow-hidden rounded-2xl border border-white/[.07] bg-[#0c1323]">
        <Fan compact open reduced={reduced} />
      </div>
    </div>
  );
}

function PublishStage() {
  return (
    <div className="grid h-full gap-3 md:grid-cols-[.9fr_1.1fr]">
      <div className="rounded-2xl border border-white/[.07] bg-[#111b30] p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-[.18em] text-cyan-300">Queue</p>
            <p className="mt-1 text-sm font-semibold">Distribuzione</p>
          </div>
          <PaperPlaneTilt size={20} className="text-cyan-300" />
        </div>
        <div className="mt-5 space-y-2">
          {[
            ['Carousel · LinkedIn', 'queued'],
            ['Reel · Instagram', 'scheduled'],
            ['Post · Facebook', 'published'],
          ].map(([label, state], index) => (
            <div key={label} className="flex items-center justify-between rounded-xl border border-white/[.06] bg-white/[.025] px-3 py-3">
              <span className="text-[10px] text-slate-300">{label}</span>
              <span className={`rounded-full px-2 py-1 text-[8px] font-semibold ${index === 2 ? 'bg-emerald-400/10 text-emerald-300' : 'bg-indigo-400/10 text-indigo-300'}`}>{state}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-2xl border border-white/[.07] bg-[#111b30] p-5">
        <div className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-[.16em] text-fuchsia-300"><CalendarBlank size={14} /> Calendario</div>
        <div className="mt-4 grid grid-cols-7 gap-2">
          {Array.from({ length: 28 }).map((_, index) => (
            <span key={index} className={`aspect-square rounded-md border ${[4, 11, 16, 23].includes(index) ? 'border-fuchsia-400/30 bg-fuchsia-400/10' : 'border-white/[.04] bg-white/[.02]'}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

function StageVisual({ index, reduced }) {
  const stages = [
    <ClientStage key="client" />,
    <DnaStage key="dna" />,
    <MasterStage key="master" />,
    <OutputStage key="output" reduced={reduced} />,
    <PublishStage key="publish" />,
  ];

  return (
    <div className="relative min-h-[430px] overflow-hidden rounded-[1.75rem] border border-white/[.08] bg-[#0d1527] p-4 shadow-2xl shadow-black/30 md:p-5" data-testid="landing-workflow-visual">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-fuchsia-400/60 to-transparent" />
      <div key={index} className={`h-full ${reduced ? '' : 'animate-[fadeIn_.35s_ease-out]'}`}>{stages[index]}</div>
    </div>
  );
}

export function LandingWorkflowStory({ steps }) {
  const [active, setActive] = useState(0);
  const reduced = useReducedMotion();
  const refs = useRef([]);

  useEffect(() => {
    if (!('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver(entries => {
      const visible = entries.filter(entry => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
      if (!visible.length) return;
      const index = Number(visible[0].target.dataset.workflowIndex);
      if (Number.isFinite(index)) setActive(index);
    }, { rootMargin: '-28% 0px -42% 0px', threshold: [0.15, 0.3, 0.5, 0.7] });

    refs.current.forEach(node => node && observer.observe(node));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="mt-12 grid gap-10 lg:grid-cols-[.78fr_1.22fr] lg:gap-14" data-testid="landing-workflow-story">
      <div className="relative">
        <div className="absolute bottom-16 left-[19px] top-16 hidden w-px bg-white/[.07] lg:block" />
        {steps.map((step, index) => {
          const Icon = ICONS[index];
          const selected = active === index;
          return (
            <article
              key={step.number}
              ref={node => { refs.current[index] = node; }}
              data-workflow-index={index}
              className="flex min-h-[42vh] items-center py-5 lg:min-h-[52vh]"
            >
              <div className={`relative w-full rounded-2xl border p-5 transition-all duration-300 ${selected ? 'border-fuchsia-400/30 bg-fuchsia-400/[.06] opacity-100' : 'border-white/[.06] bg-white/[.018] opacity-55'}`}>
                <div className={`absolute -left-[1px] top-5 hidden h-10 w-[3px] rounded-full transition-colors lg:block ${selected ? 'bg-fuchsia-400' : 'bg-transparent'}`} />
                <div className="flex items-center justify-between gap-4">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-xl border transition-colors ${selected ? 'border-fuchsia-400/25 bg-fuchsia-400/10 text-fuchsia-300' : 'border-white/[.07] bg-white/[.03] text-slate-500'}`}>
                    <Icon size={20} weight="duotone" />
                  </div>
                  <span className="text-[10px] font-bold tracking-[.16em] text-white/20">{step.number}</span>
                </div>
                <h3 className="mt-6 text-xl font-bold tracking-[-.02em]">{step.title}</h3>
                <p className="mt-3 max-w-md text-sm leading-7 text-slate-400">{step.body}</p>
              </div>
            </article>
          );
        })}
      </div>

      <div className="hidden lg:block">
        <div className="sticky top-28">
          <StageVisual index={active} reduced={reduced} />
          <div className="mt-3 flex gap-1.5">
            {steps.map((step, index) => (
              <span key={step.number} className={`h-1 flex-1 rounded-full transition-colors ${active === index ? 'bg-fuchsia-400' : 'bg-white/[.08]'}`} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function CarouselFanShowcase() {
  const reduced = useReducedMotion();
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    }, { threshold: 0.22 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="relative mx-auto mt-14 max-w-6xl overflow-hidden rounded-[2rem] border border-white/[.07] bg-gradient-to-br from-indigo-500/[.06] via-[#0d1527] to-fuchsia-500/[.06] px-4 py-8 md:px-8 md:py-10" data-testid="landing-carousel-fan">
      <div className="absolute left-1/2 top-1/2 h-56 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full bg-fuchsia-500/10 blur-3xl" />
      <div className="relative grid items-center gap-8 lg:grid-cols-[.7fr_1.3fr]">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[.2em] text-fuchsia-300">Dal Master agli output</p>
          <p className="mt-3 text-2xl font-black tracking-[-.03em] md:text-3xl">Un contenuto. Più formati. Stessa identità.</p>
          <div className="mt-5 flex flex-wrap gap-2 text-[10px] text-slate-400">
            {['Carousel', 'Reel', 'Prompted Reel', 'PDF'].map(label => (
              <span key={label} className="rounded-full border border-white/[.08] bg-white/[.03] px-3 py-1.5">{label}</span>
            ))}
          </div>
        </div>
        <div className="flex min-h-[380px] items-center justify-center overflow-hidden">
          <Fan open={visible} reduced={reduced} />
        </div>
      </div>
    </div>
  );
}
